/**
 * InitialAvatar — personfarge + forbokstav (#152, beslutning D2).
 *
 * D2: InitialAvatar i lister, chips og stabler. Pikselavataren med kosmetikk
 * (PixelAvatar / CosmeticAvatar) beholdes der kosmetikken betyr noe — Butikk,
 * Meg og Tavle-helten.
 *
 * Fyllet er personfargen (`bg-m-<slot>`) med tekstfargen som hører til
 * (`text-on-m-<slot>`), like i begge temaer. Forbokstaven er alltid med, så
 * fargen er aldri eneste kjennetegn. Navnet blir `aria-label` når avataren
 * står alene; ved siden av et synlig navn sett `decorative`.
 *
 * InitialAvatarStack legger avatarene omlott med en ring i lerretsfargen
 * (`ring-bg`), som Familie-skissen. Den er én gruppe med alle navnene som
 * tilgjengelig navn, og viser «+N» når flere enn `max` er med.
 */
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { ColorSlot } from './PixelAvatar';

/** Hele klassenavn, så Tailwind finner dem ved bygging. */
const SLOT_CLASS: Record<ColorSlot, string> = {
  'm-terracotta': 'bg-m-terracotta text-on-m-terracotta',
  'm-berry': 'bg-m-berry text-on-m-berry',
  'm-ochre': 'bg-m-ochre text-on-m-ochre',
  'm-sage': 'bg-m-sage text-on-m-sage',
  'm-sky': 'bg-m-sky text-on-m-sky',
  'm-plum': 'bg-m-plum text-on-m-plum',
};

const SLOTS = Object.keys(SLOT_CLASS) as ColorSlot[];

/**
 * Personfargen slik API-et sender den (`m-sage` …) som ColorSlot. En ukjent
 * eller manglende verdi blir `m-sage`, så avataren alltid har en farge.
 */
export function asColorSlot(token: string | null | undefined): ColorSlot {
  return SLOTS.find((s) => s === token) ?? 'm-sage';
}

function initialOf(name: string): string {
  const first = Array.from(name.trim())[0];
  return first ? first.toLocaleUpperCase('nb-NO') : '?';
}

interface InitialAvatarProps {
  name: string;
  colorSlot: ColorSlot;
  /** Diameter i px. Standard 36 (rader); 22–56 brukes i skissene. */
  size?: number;
  /** Skjul for skjermlesere når navnet står synlig ved siden av. */
  decorative?: boolean;
  className?: string;
}

export function InitialAvatar({
  name,
  colorSlot,
  size = 36,
  decorative = false,
  className,
}: InitialAvatarProps): ReactNode {
  return (
    <span
      data-testid="initial-avatar"
      data-color-slot={colorSlot}
      role={decorative ? undefined : 'img'}
      aria-label={decorative ? undefined : name}
      aria-hidden={decorative ? true : undefined}
      className={[
        'inline-flex shrink-0 items-center justify-center rounded-full font-extrabold leading-none',
        SLOT_CLASS[colorSlot],
        className,
      ]
        .filter(Boolean)
        .join(' ')}
      // Diameter og skriftstørrelse er kjøretidsverdier (som PixelAvatar sin
      // size), ikke en del av spacing-skalaen.
      style={{ width: size, height: size, fontSize: Math.round(size * 0.4) }}
    >
      {initialOf(name)}
    </span>
  );
}

interface StackMember {
  name: string;
  colorSlot: ColorSlot;
}

interface InitialAvatarStackProps {
  members: readonly StackMember[];
  size?: number;
  /** Flere enn dette vises som «+N». */
  max?: number;
  className?: string;
}

export function InitialAvatarStack({
  members,
  size = 30,
  max = 4,
  className,
}: InitialAvatarStackProps): ReactNode {
  const { t } = useTranslation();
  const shown = members.slice(0, max);
  const rest = members.length - shown.length;
  return (
    <span
      role="img"
      aria-label={members.map((m) => m.name).join(', ')}
      data-testid="initial-avatar-stack"
      className={['inline-flex items-center', className].filter(Boolean).join(' ')}
    >
      {shown.map((m, i) => (
        <InitialAvatar
          key={`${m.name}-${i}`}
          name={m.name}
          colorSlot={m.colorSlot}
          size={size}
          decorative
          className={['ring-2 ring-bg', i === 0 ? '' : '-ml-2'].filter(Boolean).join(' ')}
        />
      ))}
      {rest > 0 ? (
        <span
          aria-hidden
          className="-ml-2 inline-flex shrink-0 items-center justify-center rounded-full bg-track text-badge font-extrabold text-ink ring-2 ring-bg"
          style={{ width: size, height: size }}
        >
          {t('avatarStack.more', { count: rest })}
        </span>
      ) : null}
    </span>
  );
}
